import { useMemo } from 'react'

import { useBookkeepingBankAccounts } from '@features/accounts/hooks/useBookkeepingBankAccounts'
import { useOpenBankingAccounts } from '@features/openBanking/hooks/useOpenBankingAccounts'

import { isBankAccountConnected } from '../utils/isBankAccountConnected'
import { useFetchBankAccounts } from './useFetchBankAccounts'

interface UseHasConnectedBankAccount {
  hasConnectedBankAccount: boolean
  isLoading: boolean
}

export const useHasConnectedBankAccount = (): UseHasConnectedBankAccount => {
  const { bookkeepingBankAccounts, isLoading: isLoadingBookkeepingAccounts } = useBookkeepingBankAccounts()
  const { bankAccounts, isLoading: isLoadingBankAccounts } = useFetchBankAccounts()
  const { openBankingAccounts, isLoading: isLoadingOpenBankingAccounts } = useOpenBankingAccounts({
    withDeleted: true,
  })

  const hasConnectedBankAccount = useMemo(() => {
    const bankAccountsByAccountId = new Map(bankAccounts.map((ba) => [ba.bookkeepingAccountId, ba]))
    const openBankingAccountsByAccountId = new Map(openBankingAccounts?.map((oba) => [oba.externalAccountId, oba]))

    return bookkeepingBankAccounts
      .filter((account) => !account.isArchived)
      .some((account) => isBankAccountConnected({ account, bankAccountsByAccountId, openBankingAccountsByAccountId }))
  }, [bankAccounts, bookkeepingBankAccounts, openBankingAccounts])

  return {
    hasConnectedBankAccount,
    isLoading: isLoadingBookkeepingAccounts || isLoadingBankAccounts || isLoadingOpenBankingAccounts,
  }
}
